import {useAuthStore} from "../../store/auth.store";
import React, {useMemo, useState} from "react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {Proposal} from "../../types/Proposal";
import {loadProposals, ProposalFilter} from "../../service/proposal.service";
import {TableHeaderProps} from "../table/TableTypes";
import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Stack,
    Tooltip
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {Highlighter} from "../common/Highlighter";
import {getTypeLabel} from "../form/dropdown/ProposalTypeDropdown";
import {adaptData, adaptImageData} from "../../utils/IngestorConverters";
import dayjs from "dayjs";
import {ProposalFormToEdit} from "./ProposalFormToEdit";
import {Table} from "../table/Table";
import {ImageViewer} from "./ImageViewer";
import {containMyPhone as doesContainMyPhone} from "../../utils/PhoneUtils";
import {SupportDialog} from "../support/SupportDialog";
import {MapDialog} from "../map/MapDialog";
import {ProposalFilterComponent} from "./ProposalFilterComponent";
import {PremiumMessageElement, PremiumMessageElementProperties} from "./PremiumMessageElement";
import {usePropFilterStore} from "../../store/prop.filter.store";
import {generatePdf} from "./table/pdf";
import {SelectedProposalsBanner} from "./table/SelectedProposalsBanner";
import {imageStyle, perSquareMeter, priceRender} from "./table/proposalTableFunctions";
import {ProposalStatusCell} from "./table/ProposalStatucCell";
import {ProposalPhoneCell} from "./table/ProposalPhoneCell";
import {ProposalAddressCell} from "./table/ProposalAddressCell";
import {ProposalControlPanelCell} from "./table/ProposalControlPanelCell";


interface ProposalsTableProps {
    goBack: () => void,
    createNew: () => void,
    idToEdit?: string,
    title?: string | React.ReactNode,
    processFilter?: (filter: ProposalFilter) => ProposalFilter,
    hideMy?: boolean,
    hideFavorite?: boolean,
    onIdToEdit?: (id: string) => void
}

export function ProposalsTable({
                                   goBack,
                                   createNew,
                                   idToEdit,
                                   title,
                                   processFilter,
                                   hideMy,
                                   hideFavorite,
                                   onIdToEdit
                               }: ProposalsTableProps) {
    const {can, jwt, user} = useAuthStore();
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [selection, setSelection] = useState<Record<string, boolean>>({});
    const [selectedData, setSelectedData] = useState<Record<string, Proposal>>({});
    const [images, setImages] = useState<Proposal>();
    const [imageIndex, setImageIndex] = useState(0);
    const [favoriteLoading, setFavoriteLoading] = useState<Record<string, boolean>>({});
    const [showMap, setShowMap] = useState<Proposal>();
    const [proposalToTicket, setProposalToTicket] = useState<Proposal>();

    const [showPremiumMessageEl, setShowPremiumMessageEl] = React.useState<PremiumMessageElementProperties>({
        isPdf: false,
        isFavorite: false,
        isFavoriteInTable: false,
        isPhone: false,
        isMy: false,
        isSupport: false,
    });

    const myPhone = useMemo(() => {
        return (user?.phone || "").replaceAll(/^[+]|[\s-]/g, "");
    }, [user])
    const [containMyPhone, setContainMyPhone] = React.useState<Record<string, boolean>>({});

    const canManage = can('manage_proposals');
    const canSeePremium = can('see_premium');
    const {filter, setFilter: _setFilter} = usePropFilterStore();

    function setFilter(filter: ProposalFilter) {
        _setFilter(filter);
        setSelection({});
        setSelectedData({});
    }

    const {data, isFetching, error} = useQuery({
        queryKey: ['proposals', filter, jwt],
        refetchOnWindowFocus: false,
        queryFn: () => {
            let f = filter;
            if (processFilter) {
                f = processFilter(f);
            }
            return loadProposals(f)
                .then(response => {
                    const phones: Record<string, boolean> = {};
                    const incoming: Record<string, boolean> = {};
                    for (let i = 0; i < response.data.content.length; i++) {
                        const row = response.data.content[i];
                        incoming[row.id] = true;
                        phones[row.phone] = doesContainMyPhone(myPhone, row.phone)
                    }

                    setContainMyPhone(phones);
                    return response.data
                })
        },
        placeholderData: keepPreviousData
    });


    const anySelected = useMemo(() => {
        for (let i in selection) {
            if (selection[i]) {
                return true;
            }
        }
        return false;
    }, [selection])

    // Desktop header icon is hidden on xs; we need a separate version for mobileHeader.
    const pdfHeaderDesktop = canSeePremium
        ? <Tooltip
            sx={{display: { xs: 'none', md: 'block' }}}
            title={"You may select proposals and generate a PDF document"}><IconButton
            onClick={() => pdfGenerating ? void 0 : generatePdf(selection, (r:{}) => { setSelection(r); setSelectedData({}); }, setPdfGenerating)}>
            {pdfGenerating
                ? <CircularProgress color={'info'} size={22}/>
                : <PictureAsPdfOutlinedIcon color={anySelected ? "error" : undefined}/>}
        </IconButton></Tooltip>
        : <IconButton
            sx={{display: { xs: 'none', md: 'block' }}}
            onClick={(e) => setShowPremiumMessageEl({
                isPdf: true,
                isPhone: false,
                isFavorite: false,
                isMy: false,
                isSupport: false,
                isFavoriteInTable: false,
                element: e.currentTarget,
                isOpen: true
            })}>
            <PictureAsPdfOutlinedIcon color={"error"}/>
        </IconButton>

    const pdfHeaderMobile = canSeePremium
        ? <Tooltip title={"You may select proposals and generate a PDF document"}>
            <IconButton
                onClick={() => pdfGenerating ? void 0 : generatePdf(selection, (r:{}) => { setSelection(r); setSelectedData({}); }, setPdfGenerating)}>
                {pdfGenerating
                    ? <CircularProgress color={'info'} size={22}/>
                    : <PictureAsPdfOutlinedIcon color={anySelected ? "error" : undefined}/>}
            </IconButton>
        </Tooltip>
        : <IconButton
            onClick={(e) => setShowPremiumMessageEl({
                isPdf: true,
                isPhone: false,
                isFavorite: false,
                isMy: false,
                isSupport: false,
                isFavoriteInTable: false,
                element: e.currentTarget,
                isOpen: true
            })}>
            <PictureAsPdfOutlinedIcon color={"error"}/>
        </IconButton>

    const headers: TableHeaderProps<Proposal>[] = [
        {
            key: "selection",
            show: !canSeePremium,
            disableSort: true,
            hideMobileTitle: true,
            title: pdfHeaderDesktop,
            render: (row) => {
                return <FormControlLabel control={<Checkbox
                    onChange={(e) => setShowPremiumMessageEl({
                        isPdf: true,
                        isPhone: false,
                        isFavorite: false,
                        isFavoriteInTable: false,
                        isMy: false,
                        isSupport: false,
                        element: e.currentTarget,
                        isOpen: true
                    })}
                    checked={false}/>} label={<Highlighter text={row.id.substring(0, 6)} search={filter.id}/>}/>
            }
        },
        {
            key: "selection",
            show: canSeePremium,
            disableSort: true,
            hideMobileTitle: true,
            title: pdfHeaderDesktop,
            render: (row) => {
                return <FormControlLabel
                    control={<Checkbox
                        onChange={() => {
                            const next = !selection[row.id];
                            setSelection({...selection, [row.id]: next});
                            if (next) {
                                setSelectedData({...selectedData, [row.id]: row});
                            } else {
                                const {[row.id]: _, ...rest} = selectedData;
                                setSelectedData(rest);
                            }
                        }}
                        checked={!!(selection[row.id] as boolean|undefined)}/>
                    }
                    label={<Highlighter text={row.id.substring(0, 6)} search={filter.id}/>}
                />
            }
        },
        {
            key: "price",
            title: "Price $",
            render: (row) => <div
                className={!isFetching && (filter.priceMin || filter.priceMax) ? "filter-highlight" : ''}>
                {priceRender(row.price)}
            </div>
        },
        {
            key: "pricePm", title: "Price $/m²", render: (row) => <Stack alignItems={"center"}>
                {perSquareMeter(row.price, row.area, !isFetching && !!(filter.pricePerMeterMin || filter.pricePerMeterMax))}
            </Stack>
        },
        {
            key: "area", title: "Area m²", render: (row) => <Stack alignItems={"center"}>
                <div className={!isFetching && (filter.areaMin || filter.areaMax) ? "filter-highlight" : ''}>
                    {Math.round(row.area / 100)}
                </div>
            </Stack>
        },
        {
            key: "address",
            title: "Address",
            render:(row) => <ProposalAddressCell row={row} filter={filter} setShowMap={setShowMap} />
        },
        {
            key: "floor", title: "Floor", render: (row) => {
                return <div>{row.floor === undefined ? "?" : row.floor}{row.floorMax ? "/" + row.floorMax : ''}</div>
            }
        },
        {
            key: "rooms", title: "Rooms", render: (row) => {
                return row.type ? getTypeLabel(adaptData(row.source, 'type', row.type)) : row.rooms;
            }
        },
        {
            key: "photos", disableSort: true, title: "Photos", className: "no-padding", render: (row) => {
                return row.resources && row.resources.length > 0
                    ? <div className={"proposal-table-images"}>
                        {row.resources
                            .filter((_, index) => index <= 3)
                            .map((resource, index) => <Button sx={{padding: 0, minWidth: 'initial'}} key={resource.id}
                                                              onClick={() => {
                                                                  setImageIndex(index)
                                                                  setImages(row)
                                                              }}>
                                <img alt={"source"} src={adaptImageData(resource.source, 'content', resource.content)}
                                     style={imageStyle}/>
                            </Button>)}
                    </div>
                    : <span/>
            }
        },
        {
            key: "phone",
            disableSort: true,
            title: "Phone",
            render: (row) => <ProposalPhoneCell
                filter={filter}
                row={row}
                setShowPremiumMessageEl={setShowPremiumMessageEl}/>
        },
        {
            key: "name",
            disableSort: true,
            title: "Name",
            show: canManage,
            render: (row) => <Highlighter text={row.name} search={filter.name}/>
        },
        {
            key: "createdAt",
            title: "Created At",
            render: (row) => {
                return <Stack>
                    <div>{dayjs(row.createdAt).format('YYYY-MM-DD HH:mm')}</div>
                    {canManage && <div>{row.createdAtExternal}</div>}
                </Stack>
            }
        },
        {
            key: "adminCheckDate",
            show: canManage,
            title: "Check Date",
            render: row => {
                return <div>
                    {row.adminCheckDate && dayjs(row.adminCheckDate).format('YYYY-MM-DD HH:mm')}
                </div>
            },
        },
        {
            key: "proposalStatus",
            className: 'no-padding',
            title: "Status",
            render: (row) => <ProposalStatusCell
                row={row}
                filter={filter}
                containMyPhone={containMyPhone[row.phone]}/>
        },
        {
            key: "controlPanel",
            disableSort: true,
            title: <Stack flexDirection={"row-reverse"}>
                <Button onClick={() => createNew()}>ADD LISTING</Button>
            </Stack>,
            render: (row) => <ProposalControlPanelCell
                row={row}
                filter={filter}
                containMyPhone={containMyPhone[row.phone]}
                favoriteLoading={favoriteLoading}
                setFavoriteLoading={setFavoriteLoading}
                onIdToEdit={onIdToEdit}
                setProposalToTicket={setProposalToTicket}
                setShowPremiumMessageEl={setShowPremiumMessageEl}
            />
        }
    ]


    if (idToEdit) {
        return <ProposalFormToEdit id={idToEdit} goBack={goBack}/>
    }

    return <>
        {error && <Alert color={"error"}>{error?.name + ": " + error?.message}</Alert>}
        {title ? title : undefined}
        <ProposalFilterComponent
            filter={filter}
            setFilter={setFilter}
            hideFavorite={hideFavorite}
            hideMy={hideMy}
            setShowPremiumMessageEl={setShowPremiumMessageEl}/>
        <SelectedProposalsBanner
               selectedData={selectedData}
               currentPageProposals={(data?.content || [])}
               onRemove={(id) => {
                   setSelection(prev => { const {[id]: _, ...rest} = prev; return rest; });
                   setSelectedData(prev => { const {[id]: _, ...rest} = prev; return rest; });
               }}
        />
        <Table data={data?.content || []}
               mobileHeader={pdfHeaderMobile}
               sort={{
                   key: filter.sortKey,
                   order: filter.sortOrder,
                   onChange: (key, order) => setFilter({...filter, page: 0, sortKey: key, sortOrder: order})
               }}
               isLoading={isFetching}
               total={data?.totalElements || 0}
               page={filter.page}
               size={filter.size}
               headers={headers}
               pageChange={(page, size) => {
                   _setFilter({...filter, page, size});
                   localStorage.setItem("proposals_page_size", size.toString());
               }}/>

        {proposalToTicket &&
            <SupportDialog proposal={proposalToTicket} onClose={() => setProposalToTicket(undefined)}/>}
        {images && <ImageViewer resources={images.resources} index={imageIndex} onClose={() => setImages(undefined)}/>}
        {showMap &&
            <MapDialog minWidth={500} address={showMap.address} maxWidth={900} lat={showMap.lat} lng={showMap.lng} onClose={() => setShowMap(undefined)}/>}
        <PremiumMessageElement setShowPremiumMessageEl={setShowPremiumMessageEl}
                               showPremiumMessageEl={showPremiumMessageEl}/>
    </>
}


